import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { SharedModule } from "../../../../shared/shared.module";
import { UiFacadeService } from "../../../user_infos/services/store/ui-facade.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { OF } from "../../../of/models/of.model";
import { Travail } from "../../../info-journaliere/models/travail.model";
import { format } from "date-fns";

@Component({
  selector: "vl-widget-productivite-user-infos",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, WidgetPeriodFiltersComponent, SharedModule, ChipsToggleComponent, LoaderSkeletonComponent],
  templateUrl: "./widget-productivite-user-infos.component.html",
  styleUrls: ["./widget-productivite-user-infos.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "widget-component" },
})
export class WidgetProductiviteUserInfosComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  editable = input(true);

  private uiFacade = inject(UiFacadeService);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  stats = this.uiFacade.getState().stats;

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("prodChart");
  chart?: Chart;

  period = signal<string>("m");
  timeScale = signal<string>("MAX");
  comparison = signal<string | null>(null);
  selectedFamille = signal<string>("ALL");

  private static FAMILLE_COLORS = [
    "hsl(210 70% 55%)", "hsl(35 90% 55%)", "hsl(140 70% 45%)",
    "hsl(280 60% 55%)", "hsl(0 70% 55%)", "hsl(170 60% 45%)",
    "hsl(45 90% 50%)", "hsl(320 60% 55%)",
  ];

  /** Map ofId → famille technique denomination (via paramLigne or articleInfos.sousFamille) */
  private familleMap = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    const map = new Map<string, string>();
    for (const of of ofs) {
      if (!of.id || map.has(of.id)) continue;
      const denom =
        of.paramLigne?.familleTechnique?.denomination ??
        of.articleInfos?.sousFamille?.denomination ??
        "";
      map.set(of.id, denom);
    }
    return map;
  });

  familleElements = computed(() => {
    const familles = new Set(this.familleMap().values());
    familles.delete("");
    const sorted = [...familles].sort();
    return [
      { name: TranslateUtils.translate("stats_ligne.toutes"), value: "ALL" },
      ...sorted.map((f) => ({ name: f, value: f })),
    ];
  });

  private parseDureeToHours(s: unknown): number {
    if (!s) return 0;
    const str = String(s).trim();
    let m = /^(\d{1,3})\s*h\s*(\d{1,2})?$/i.exec(str);
    if (m) return parseNumber(m[1] || 0) + parseNumber(m[2] || 0) / 60;
    m = /^(\d{1,3}):(\d{1,2})$/.exec(str);
    if (m) return parseNumber(m[1] || 0) + parseNumber(m[2] || 0) / 60;
    m = /^(\d+(?:[.,]\d+)?)$/.exec(str);
    if (m) return parseNumber(m[1].replace(",", "."));
    return 0;
  }

  private static scoreColor(productivity: number): string {
    if (productivity >= 100) return "hsl(140 70% 45%)";
    if (productivity >= 50) return "hsl(35 90% 55%)";
    return "hsl(0 70% 55%)";
  }

  /** Compute productivity entries (qty/hours) by period key */
  private computeProductivityEntries(ofs: OF[], travail: Travail[], p: string): [string, number][] {
    const hoursByKey = new Map<string, number>();
    for (const t of travail) {
      if (!t.date) continue;
      const key = this.dateKey(new Date(t.date), p);
      hoursByKey.set(key, (hoursByKey.get(key) ?? 0) + this.parseDureeToHours(t.duree));
    }

    const qtyByKey = new Map<string, number>();
    for (const of of ofs) {
      if (!of.dateFin) continue;
      const key = this.dateKey(new Date(of.dateFin), p);
      qtyByKey.set(key, (qtyByKey.get(key) ?? 0) + parseNumber(of.quantiteProduite ?? 0));
    }

    const allKeys = [...new Set([...hoursByKey.keys(), ...qtyByKey.keys()])].sort();
    return allKeys
      .map((key) => {
        const hours = hoursByKey.get(key) ?? 0;
        const qty = qtyByKey.get(key) ?? 0;
        const productivity = hours > 0 ? qty / hours : 0;
        return [key, Math.round(productivity * 100) / 100] as [string, number];
      })
      .filter(([, v]) => v > 0);
  }

  /** Shift the year part of a period key by delta (e.g., "2024-03" → "2023-03") */
  private shiftKeyYear(key: string, delta: number): string {
    const m = key.match(/^(\d{4})(.*)/);
    if (m) return `${parseInt(m[1]) + delta}${m[2]}`;
    return key;
  }

  chartData = computed(() => {
    const allOfs: OF[] = this.stats()?.ofs ?? [];
    const travail: Travail[] = this.stats()?.travail ?? [];
    const p = this.period();
    const ts = this.timeScale();
    const comp = this.comparison();
    const famille = this.selectedFamille();
    const fMap = this.familleMap();

    if (!allOfs.length || !travail.length) {
      return { mode: "single" as const, labels: [] as string[], values: [] as number[], colors: [] as string[], stacked: false };
    }

    const ofs = famille === "ALL"
      ? allOfs
      : allOfs.filter((of) => fMap.get(of.id) === famille);

    // Comparison mode → collapse to single total + comparison dataset
    if (comp) {
      return this.buildComparisonData(ofs, travail, p, ts, comp);
    }

    // Multi-family stacked mode (no comparison)
    if (famille === "ALL") {
      const familles = [...new Set([...fMap.values()].filter(Boolean))].sort();
      if (!familles.length || familles.length <= 1) {
        return this.buildSingleData(ofs, travail, p, ts);
      }

      const monthSet = new Set<string>();
      for (const of of allOfs) {
        if (!of.dateFin) continue;
        monthSet.add(this.dateKey(new Date(of.dateFin), p));
      }
      const allKeys = [...monthSet].sort();
      const filtered = this.filterByScale(allKeys.map((k) => [k, 0] as [string, number]), ts).map(([k]) => k);
      if (!filtered.length) return { mode: "multi" as const, labels: [] as string[], datasets: [] as any[], stacked: true };

      const filteredSet = new Set(filtered);

      // Group hours by period (total hours — not per family)
      const hoursByKey = new Map<string, number>();
      for (const t of travail) {
        if (!t.date) continue;
        const key = this.dateKey(new Date(t.date), p);
        if (!filteredSet.has(key)) continue;
        hoursByKey.set(key, (hoursByKey.get(key) ?? 0) + this.parseDureeToHours(t.duree));
      }

      // Each family's qty / total hours = contribution to total productivity (stacked)
      const datasets = familles.map((f, i) => {
        const qtyByKey = new Map<string, number>();
        for (const of of allOfs) {
          if (!of.dateFin || fMap.get(of.id) !== f) continue;
          const key = this.dateKey(new Date(of.dateFin), p);
          if (!filteredSet.has(key)) continue;
          qtyByKey.set(key, (qtyByKey.get(key) ?? 0) + parseNumber(of.quantiteProduite ?? 0));
        }
        return {
          label: f,
          data: filtered.map((k) => {
            const hours = hoursByKey.get(k) ?? 0;
            const qty = qtyByKey.get(k) ?? 0;
            return hours > 0 ? Math.round((qty / hours) * 100) / 100 : null;
          }),
          backgroundColor: WidgetProductiviteUserInfosComponent.FAMILLE_COLORS[i % WidgetProductiviteUserInfosComponent.FAMILLE_COLORS.length],
          borderRadius: 4,
        };
      });

      return {
        mode: "multi" as const,
        labels: filtered.map((k) => this.formatLabel(k)),
        datasets,
        stacked: true,
      };
    }

    return this.buildSingleData(ofs, travail, p, ts);
  });

  private buildSingleData(ofs: OF[], travail: Travail[], p: string, ts: string) {
    const entries = this.computeProductivityEntries(ofs, travail, p);
    const filtered = this.filterByScale(entries, ts);

    return {
      mode: "single" as const,
      labels: filtered.map(([k]) => this.formatLabel(k)),
      values: filtered.map(([, v]) => v),
      colors: filtered.map(([, v]) => WidgetProductiviteUserInfosComponent.scoreColor(v)),
      stacked: false,
    };
  }

  private buildComparisonData(ofs: OF[], travail: Travail[], p: string, ts: string, comp: string) {
    const allEntries = this.computeProductivityEntries(ofs, travail, p);
    const current = this.filterByScale(allEntries, ts);
    if (!current.length) return { mode: "multi" as const, labels: [] as string[], datasets: [] as any[], stacked: false };

    const prodLabel = TranslateUtils.translate("stats_user_infos.productivite");
    let compData: (number | null)[];
    let compLabel: string;

    if (comp === "n-1") {
      compLabel = TranslateUtils.translate("dashboard_ui.n_moins_1");
      compData = current.map(([key]) => {
        const shifted = this.shiftKeyYear(key, -1);
        const entry = allEntries.find(([k]) => k === shifted);
        return entry ? entry[1] : null;
      });
    } else {
      // "previous" — take same number of preceding periods
      compLabel = TranslateUtils.translate("dashboard_ui.periode_precedente");
      const n = current.length;
      const beforeCurrent = allEntries.filter(([k]) => k < current[0][0]);
      const prevSlice = beforeCurrent.slice(-n);
      compData = current.map((_, i) => prevSlice[i]?.[1] ?? null);
    }

    return {
      mode: "multi" as const,
      labels: current.map(([k]) => this.formatLabel(k)),
      datasets: [
        {
          label: prodLabel,
          data: current.map(([, v]) => v),
          backgroundColor: "hsl(210 70% 55%)",
          borderRadius: 4,
        },
        {
          label: compLabel,
          data: compData,
          backgroundColor: "hsl(210 70% 55% / 0.4)",
          borderRadius: 4,
        },
      ],
      stacked: false,
    };
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.destroyChart());

    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private dateKey(d: Date, period: string): string {
    switch (period) {
      case "j":
        return format(d, "yyyy-MM-dd");
      case "s":
        return format(d, "yyyy-'S'ww");
      case "a":
        return format(d, "yyyy");
      case "m":
      default:
        return format(d, "yyyy-MM");
    }
  }

  private formatLabel(key: string): string {
    const m = key.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${WidgetHelperService.MONTHS_SHORT[parseInt(m[2], 10) - 1]} ${m[1].slice(2)}`;
    const s = key.match(/^\d{4}-S(\d{2})$/);
    if (s) return `S${s[1]}`;
    const d = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (d) return `${d[3]}/${d[2]}`;
    return key;
  }

  private filterByScale(entries: [string, number][], scale: string): [string, number][] {
    if (!entries.length) return entries;
    const now = new Date();
    let cutoff: Date;
    switch (scale) {
      case "1 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case "6 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case "1 a":
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case "5 a":
        cutoff = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
        break;
      case "MAX":
        return entries;
      case "YTD":
      default:
        cutoff = new Date(now.getFullYear(), 0, 1);
        break;
    }
    const cutKey = this.dateKey(cutoff, this.period());
    return entries.filter(([k]) => k >= cutKey);
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(data: any) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const textColor =
      getComputedStyle(this.elementRef.nativeElement)
        .getPropertyValue("--color-on-background-alt")
        .trim() || "#888";

    const isMulti = data.mode === "multi";
    const isStacked = !!data.stacked;

    const datasets = isMulti
      ? data.datasets
      : [
          {
            label: TranslateUtils.translate("stats_user_infos.productivite"),
            data: data.values,
            backgroundColor: data.colors,
            borderRadius: 4,
          },
        ];

    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: { labels: data.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: isMulti,
            position: "bottom",
            labels: { color: textColor, boxWidth: 12, padding: 8, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${isMulti ? ctx.dataset.label + ": " : ""}${ctx.parsed.y?.toFixed(1) ?? 0} /h`,
            },
          },
        },
        scales: {
          x: {
            stacked: isStacked,
            grid: { display: false },
            ticks: { color: textColor, maxRotation: 45, font: { size: 10 } },
          },
          y: {
            stacked: isStacked,
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 10 },
              callback: (value) => WidgetHelperService.formatNumeric(+value),
            },
          },
        },
      },
    };
    this.chart = new Chart(ctx, config);
  }
}
